/**
 * Rango Quantum — filtros de producción (WP H-G, 2026-10-26).
 *
 * Port de backend/research/rango_quantum/filters.py PERO con la semántica
 * POR VELA con la que opera el bot de verdad, no la del estudio: producción
 * evalúa cada vela con la ventana de las últimas 2000 velas cerradas
 * (app/tasks/rango_quantum_live_data.py::WINDOW_BARS) — mismo cálculo que el
 * Radar en modo per_candle (app/radar/detect.py::_run_rango:
 * df.iloc[max(0, i-1999): i+1] + sct._rango_flip_direction, guard de
 * WARMUP_BARS + 100 velas en signal_card_tasks.py).
 *
 * Diferencia deliberada con filters.py: allí `_mask_atr_min_median` toma la
 * mediana de TODO el frame pasado (correcto para prod, que solo evalúa la
 * última vela de una ventana de 2000); aquí la mediana es RODANTE por vela
 * (las últimas 2000 hasta esa vela, incluida), que es exactamente lo que el
 * bot habría evaluado en esa vela. NaN ignorados como pandas (.median()
 * skipna=True): media de los dos centrales si la ventana tiene nº par de
 * valores válidos.
 *
 * Cualquier cambio de lógica se hace primero en Python (filters.py /
 * signal_card_tasks.py) y se replica aquí — lo garantiza
 * engine.parity.live.test.js (señales filtradas JS == las que el camino del
 * bot genera sobre las mismas velas BingX).
 */
import { WARMUP_BARS } from './engine.js'

// Mismos umbrales que filters.py::_mask_rsi_narrow (exclusivos en los dos lados).
export const RSI_NARROW_LONG = Object.freeze({ min: 55, max: 70 })
export const RSI_NARROW_SHORT = Object.freeze({ min: 30, max: 45 })

// = WINDOW_BARS de app/tasks/rango_quantum_live_data.py: la mediana de ATR%
// de cada vela se calcula sobre las últimas 2000 velas hasta ella (incluida).
export const ATR_MEDIAN_WINDOW_BARS = 2000

// Guard de prod (signal_card_tasks.py::_rango_flip_direction y
// _process_symbol: el frame necesita WARMUP_BARS + 100 velas): el bot nunca
// evalúa una vela sin 1100 velas de historia — con filtros activos el chart
// tampoco pinta antes de ese índice.
export const MIN_EVAL_BAR_INDEX = WARMUP_BARS + 99

export const KNOWN_FILTER_NAMES = Object.freeze(['rsi_narrow', 'atr_min_median'])

function bisectLeft(arr, v) {
  let lo = 0
  let hi = arr.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (arr[mid] < v) lo = mid + 1
    else hi = mid
  }
  return lo
}

/**
 * Máscara atr_min_median por vela: atrPct[i] > mediana de atrPct de la
 * ventana [max(0, i-1999), i] (NaN fuera, como pandas). Ventana deslizante
 * ordenada: O(n · w) de facto con el splice, exacta frente a pandas median
 * (n impar → central; n par → media de los dos centrales).
 */
function atrMinMedianMask(atrPct) {
  const n = atrPct.length
  const out = new Array(n).fill(false)
  const win = [] // valores válidos de la ventana, ordenados
  for (let i = 0; i < n; i++) {
    const v = atrPct[i]
    if (Number.isFinite(v)) win.splice(bisectLeft(win, v), 0, v)
    const outIdx = i - ATR_MEDIAN_WINDOW_BARS
    if (outIdx >= 0) {
      const old = atrPct[outIdx]
      if (Number.isFinite(old)) win.splice(bisectLeft(win, old), 1)
    }
    if (win.length === 0 || !Number.isFinite(v)) continue
    const m = win.length >> 1
    const median = win.length % 2 === 1 ? win[m] : (win[m - 1] + win[m]) / 2
    out[i] = v > median
  }
  return out
}

/**
 * Aplica los filtros de producción (AND) sobre las filas de
 * computeSignalFrame y devuelve filas NUEVAS (no muta `frame`). Con
 * `filterNames` vacío devuelve `frame` tal cual (pintado sin filtros — la
 * divergencia con el bot la avisa el badge, WP H-G). Con filtros activos,
 * ninguna vela anterior a MIN_EVAL_BAR_INDEX pinta señal (guard de prod).
 * Lanza con un nombre de filtro desconocido (resolveConfig ya los sanea —
 * llegar aquí con uno es error de programación, como el ValueError de
 * filters.py::apply_filter).
 */
export function applyProductionFilters(frame, filterNames) {
  if (filterNames == null || filterNames.length === 0) return frame
  for (const name of filterNames) {
    if (!KNOWN_FILTER_NAMES.includes(name)) throw new Error(`Filtro desconocido: ${name}`)
  }

  let rsiMask = null
  if (filterNames.includes('rsi_narrow')) {
    rsiMask = frame.map((row) => ({
      long: row.rsi > RSI_NARROW_LONG.min && row.rsi < RSI_NARROW_LONG.max,
      short: row.rsi < RSI_NARROW_SHORT.max && row.rsi > RSI_NARROW_SHORT.min,
    }))
  }
  let atrMask = null
  if (filterNames.includes('atr_min_median')) {
    atrMask = atrMinMedianMask(frame.map((row) => row.atr / row.close))
  }

  return frame.map((row, i) => {
    if (i < MIN_EVAL_BAR_INDEX) return { ...row, longCond: false, shortCond: false }
    const longOk = (rsiMask == null || rsiMask[i].long) && (atrMask == null || atrMask[i])
    const shortOk = (rsiMask == null || rsiMask[i].short) && (atrMask == null || atrMask[i])
    return { ...row, longCond: row.longCond && longOk, shortCond: row.shortCond && shortOk }
  })
}
